/**
 * One-step equation and inequality solving for 6th-grade standards.
 */
import type { Rational } from "./rational";
import type { InequalityOp } from "./answer";

export type OneStepOperation = "add" | "subtract" | "multiply" | "divide";

export interface OneStepEquation {
  readonly variable: string;
  readonly operation: OneStepOperation;
  readonly operand: Rational;
  readonly result: Rational;
  readonly solution: Rational;
}

/** Build a one-step equation around a known solution. */
export function buildEquation(
  variable: string,
  operation: OneStepOperation,
  operand: Rational,
  solution: Rational,
): OneStepEquation {
  let result: Rational;
  switch (operation) {
    case "add":      result = solution.add(operand); break;
    case "subtract": result = solution.sub(operand); break;
    case "multiply": result = solution.mul(operand); break;
    case "divide":   result = solution.div(operand); break;
  }
  return { variable, operation, operand, result, solution };
}

/** Independently solve to verify. */
export function solveEquation(eq: OneStepEquation): Rational {
  switch (eq.operation) {
    case "add":      return eq.result.sub(eq.operand);
    case "subtract": return eq.result.add(eq.operand);
    case "multiply": return eq.result.div(eq.operand);
    case "divide":   return eq.result.mul(eq.operand);
  }
}

export function validateEquation(eq: OneStepEquation): boolean {
  return solveEquation(eq).equals(eq.solution);
}

export function equationToLatex(eq: OneStepEquation): string {
  const v = eq.variable;
  const op = eq.operand.toLatex();
  const res = eq.result.toLatex();
  switch (eq.operation) {
    case "add":      return `${v} + ${op} = ${res}`;
    case "subtract": return `${v} - ${op} = ${res}`;
    case "multiply": return eq.operand.isInteger() ? `${op}${v} = ${res}` : `${op} \\cdot ${v} = ${res}`;
    case "divide":   return `\\frac{${v}}{${op}} = ${res}`;
  }
}

export function equationToText(eq: OneStepEquation): string {
  const v = eq.variable;
  const op = eq.operand.toString();
  const res = eq.result.toString();
  switch (eq.operation) {
    case "add":      return `${v} + ${op} = ${res}`;
    case "subtract": return `${v} - ${op} = ${res}`;
    case "multiply": return `${op} * ${v} = ${res}`;
    case "divide":   return `${v} / ${op} = ${res}`;
  }
}

export function deriveSteps(eq: OneStepEquation): Array<{ description: string; latex: string }> {
  const v = eq.variable;
  switch (eq.operation) {
    case "add":
      return [
        { description: `Subtract ${eq.operand.toString()} from both sides`, latex: `${v} = ${eq.result.toLatex()} - ${eq.operand.toLatex()}` },
        { description: "Simplify", latex: `${v} = ${eq.solution.toLatex()}` },
      ];
    case "subtract":
      return [
        { description: `Add ${eq.operand.toString()} to both sides`, latex: `${v} = ${eq.result.toLatex()} + ${eq.operand.toLatex()}` },
        { description: "Simplify", latex: `${v} = ${eq.solution.toLatex()}` },
      ];
    case "multiply":
      return [
        { description: `Divide both sides by ${eq.operand.toString()}`, latex: `${v} = \\frac{${eq.result.toLatex()}}{${eq.operand.toLatex()}}` },
        { description: "Simplify", latex: `${v} = ${eq.solution.toLatex()}` },
      ];
    case "divide":
      return [
        { description: `Multiply both sides by ${eq.operand.toString()}`, latex: `${v} = ${eq.result.toLatex()} \\times ${eq.operand.toLatex()}` },
        { description: "Simplify", latex: `${v} = ${eq.solution.toLatex()}` },
      ];
  }
}

export interface OneStepInequality {
  readonly variable: string;
  readonly operation: OneStepOperation;
  readonly operand: Rational;
  readonly result: Rational;
  readonly op: InequalityOp;
  readonly solution: Rational;
  readonly solutionOp: InequalityOp;
}

export function buildInequality(
  variable: string,
  operation: OneStepOperation,
  operand: Rational,
  solution: Rational,
  op: InequalityOp,
): OneStepInequality {
  let result: Rational;
  switch (operation) {
    case "add":      result = solution.add(operand); break;
    case "subtract": result = solution.sub(operand); break;
    case "multiply": result = solution.mul(operand); break;
    case "divide":   result = solution.div(operand); break;
  }
  let solutionOp = op;
  if ((operation === "multiply" || operation === "divide") && operand.isNegative()) {
    const flipMap: Record<InequalityOp, InequalityOp> = { "<": ">", ">": "<", "<=": ">=", ">=": "<=" };
    solutionOp = flipMap[op];
  }
  return { variable, operation, operand, result, op, solution, solutionOp };
}
